const RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export type SSEEvent = {
    type: string;
    id?: string;
    data: string;
};

type SubscribeToSSEOptions = {
    url: string;
    abortSignal?: AbortSignal;
    getAccessToken?: (options?: { forceRefresh?: boolean }) => Promise<string | null>;
    /** Called when the server still rejects the token after a forced refresh. */
    onUnauthorized?: () => void;
    /** Called for every failed connection that is about to be retried. */
    onError?: (error: unknown) => void;
    /** Defaults to retrying every failure. Returning false throws the error to the caller. */
    shouldRetry?: (error: unknown) => boolean;
};

export class SSEError extends Error {
    readonly status: number;

    constructor(status: number) {
        super(`SSE stream failed with status ${status}`);
        this.status = status;
    }
}

/**
 * Yields the events of an SSE endpoint until `abortSignal` fires, reconnecting
 * after every end or failure and resuming with `Last-Event-ID`.
 */
export async function* subscribeToSSE(
    options: SubscribeToSSEOptions,
): AsyncIterable<SSEEvent> {
    const { abortSignal, shouldRetry = () => true } = options;
    let lastEventId: string | undefined;
    let attempt = 0;

    while (!abortSignal?.aborted) {
        try {
            const body = await openSSE(options, lastEventId);
            attempt = 0;
            for await (const event of parseSSE(body, abortSignal)) {
                if (event.id) {
                    lastEventId = event.id;
                }
                yield event;
            }
        } catch (error) {
            if (abortSignal?.aborted) {
                return;
            }
            if (!shouldRetry(error)) {
                throw error;
            }
            options.onError?.(error);
        }

        await waitForReconnect(attempt++, abortSignal);
    }
}

async function openSSE(
    options: SubscribeToSSEOptions,
    lastEventId: string | undefined,
): Promise<ReadableStream<Uint8Array>> {
    const { url, abortSignal, getAccessToken, onUnauthorized } = options;

    let response = await fetchSSE(
        url,
        abortSignal,
        lastEventId,
        await getAccessToken?.(),
    );
    if (response.status === 401) {
        const refreshedToken = (await getAccessToken?.({ forceRefresh: true })) ?? null;
        if (refreshedToken === null) {
            onUnauthorized?.();
            throw new SSEError(401);
        }
        response = await fetchSSE(url, abortSignal, lastEventId, refreshedToken);
        if (response.status === 401) {
            onUnauthorized?.();
        }
    }

    if (!response.ok) {
        throw new SSEError(response.status);
    }
    if (!response.body) {
        throw new Error("SSE response has no body");
    }
    return response.body;
}

/** `EventSource` cannot send an `Authorization` header, so streams are read through `fetch`. */
function fetchSSE(
    url: string,
    abortSignal: AbortSignal | undefined,
    lastEventId: string | undefined,
    accessToken: string | null | undefined,
): Promise<Response> {
    const headers = new Headers({ Accept: "text/event-stream" });
    if (lastEventId) {
        headers.set("Last-Event-ID", lastEventId);
    }
    if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return fetch(url, { credentials: "omit", headers, signal: abortSignal });
}

async function* parseSSE(
    body: ReadableStream<Uint8Array>,
    abortSignal: AbortSignal | undefined,
): AsyncIterable<SSEEvent> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (!abortSignal?.aborted) {
            const { done, value } = await reader.read();
            buffer += decoder.decode(value, { stream: !done });

            let boundary = /\r?\n\r?\n/.exec(buffer);
            while (boundary) {
                const event = parseEventBlock(buffer.slice(0, boundary.index));
                buffer = buffer.slice(boundary.index + boundary[0].length);
                if (event) {
                    yield event;
                }
                boundary = /\r?\n\r?\n/.exec(buffer);
            }

            if (done) {
                return;
            }
        }
    } finally {
        reader.releaseLock();
    }
}

function parseEventBlock(block: string): SSEEvent | null {
    let type: string | undefined;
    let id: string | undefined;
    const data: string[] = [];

    for (const line of block.split(/\r?\n/)) {
        if (line.startsWith(":")) {
            continue;
        }
        if (line.startsWith("event:")) {
            type = line.slice("event:".length).trimStart();
        } else if (line.startsWith("id:")) {
            id = line.slice("id:".length).trimStart();
        } else if (line.startsWith("data:")) {
            data.push(line.slice("data:".length).trimStart());
        }
    }

    if (type === undefined && data.length === 0) {
        return null;
    }
    return { type: type ?? "message", id, data: data.join("\n") };
}

function waitForReconnect(
    attempt: number,
    abortSignal: AbortSignal | undefined,
): Promise<void> {
    return new Promise((resolve) => {
        if (abortSignal?.aborted) {
            resolve();
            return;
        }

        const onAbort = () => {
            clearTimeout(timeout);
            resolve();
        };
        const timeout = setTimeout(() => {
            abortSignal?.removeEventListener("abort", onAbort);
            resolve();
        }, getReconnectDelayWithBackoff(attempt));
        abortSignal?.addEventListener("abort", onAbort, { once: true });
    });
}

function getReconnectDelayWithBackoff(attempt: number): number {
    const delay = Math.min(RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
    return delay * (0.5 + Math.random() / 2);
}
